import React, { useState } from "react";
import {
  StyledAssignedGoalTable,
  StyledWorkInformationBody,
} from "../Resume.styled";
import { IconManager } from "../../../../../components/core/IconManager/IconManager";
import { FontWeightEnum, IconSizeEnum } from "../../../../../types/AppTypes";
import { BodyTyp } from "../../../../../components/Typography/Typography";
import {
  ACTION_LABEL,
  ASSIGNED_GOAL_LABEL,
  CONTEST_LABEL,
  EMPTY_STRING,
  GOALS_LABEL,
  HIGH_IMPACT_LABEL,
  IMPACT_LABEL,
  LOW_IMPACT_LABEL,
  MEDIUM_IMPACT_LABEL,
  NO_IMPACT_LABEL,
  PERCENT_REACHED_GOALS_LABEL,
  TIME_LABEL,
} from "../../../../../constants/Dictionary";
import { GetCollaboratorCmSheetGoalsResponse } from "../../../../../types/api/collaborators.type";
import { ReactComponent as TargetIcon } from "../../../../../icons/target.svg";
import {
  Accordion,
  AccordionVariantEnum,
} from "../../../../../components/core/Accordion/Accordion";
import {
  StyledImpactBoxWrapper,
  StyledTableCell,
  StyledTableCellContent,
  StyledTableHeader,
  StyledTableHeaderContent,
  StyledTableRow,
} from "../../SchedaCM/Goals/Goals.styled";
import { TableCellAlignEnum } from "../../../../../components/core/Table/Table";
import { theme } from "../../../../../constants/StyledComponents";
import { SelectOption } from "../../../../../components/core/Select/Select";
import { ImpactBox } from "../../../../../components/ImpactBox/ImpactBox";
import { roleHasPermission } from "../../../../../utils/roleAuth.utils";
import { PermissionsEnum } from "../../../../../constants/AppConstants";
import { useAppSelector } from "../../../../../hooks/store.hook";
import { Tooltip } from "../../../../../components/core/Tooltip/Tooltip";

type TableHeaderProps = {
  align?: TableCellAlignEnum;
  label: string;
};

const TableHeader = ({ align, label }: TableHeaderProps) => (
  <StyledTableHeader title={label}>
    <StyledTableHeaderContent $align={align}>
      <Tooltip message={label} autoWidth>
        <BodyTyp variant="2" fontWeight={FontWeightEnum.SEMIBOLD}>
          {label}
        </BodyTyp>
      </Tooltip>
    </StyledTableHeaderContent>
  </StyledTableHeader>
);

type TableCellProps = {
  edit?: boolean;
  align?: TableCellAlignEnum;
  children: React.ReactNode;
};

const TableCell = ({ edit, align, children }: TableCellProps) => (
  <StyledTableCell $edit={edit}>
    <StyledTableCellContent $align={align}>{children}</StyledTableCellContent>
  </StyledTableCell>
);

export const AssignedGoal = ({
  data,
}: {
  data: GetCollaboratorCmSheetGoalsResponse | undefined;
}) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);

  const { profile } = useAppSelector((state) => state.user);

  const getTableGoalColumns = (): {
    label: string;
    align: TableCellAlignEnum;
    tooltip?: string;
  }[] => {
    const tableColumns = [
      { label: GOALS_LABEL, align: TableCellAlignEnum.LEFT },
      {
        label: ACTION_LABEL,
        align: TableCellAlignEnum.CENTER,
      },
      {
        label: CONTEST_LABEL,
        align: TableCellAlignEnum.CENTER,
      },
      {
        label: TIME_LABEL,
        align: TableCellAlignEnum.CENTER,
      },
      {
        label: IMPACT_LABEL,
        align: TableCellAlignEnum.CENTER,
      },
      {
        label: PERCENT_REACHED_GOALS_LABEL,
        align: TableCellAlignEnum.CENTER,
      },
    ];

    return tableColumns;
  };

  const filteredFields = getTableGoalColumns();

  const evaluationOptions: { key: string; values: SelectOption<number>[] }[] = [
    {
      key: "actionChallenge",
      values: [
        {
          label: NO_IMPACT_LABEL,
          value: 0,
          description: "Nessuna sfida rilevata",
        },
        {
          label: LOW_IMPACT_LABEL,
          value: 1,
          description:
            "Attività esecutive, procedure definite, margine decisionale limitato nel proprio perimetro",
        },
        {
          label: MEDIUM_IMPACT_LABEL,
          value: 2,
          description:
            "Scelte operative autonome all’interno del proprio perimetro",
        },
        {
          label: HIGH_IMPACT_LABEL,
          value: 3,
          description:
            "Decisioni complesse, ambigue, con impatto rilevante sugli outcome del proprio perimetro",
        },
      ],
    },
    {
      key: "contestChallenge",
      values: [
        {
          label: NO_IMPACT_LABEL,
          value: 0,
          description: "Nessuna sfida rilevata",
        },
        {
          label: LOW_IMPACT_LABEL,
          value: 1,
          description: "Contesto stabile, prevedibile, ben strutturato",
        },
        {
          label: MEDIUM_IMPACT_LABEL,
          value: 2,
          description: "Interazioni multiple, variabilità gestibile",
        },
        {
          label: HIGH_IMPACT_LABEL,
          value: 3,
          description:
            "Elevata incertezza, cambiamenti frequenti, forte esposizione a fattori esterni",
        },
      ],
    },
    {
      key: "timeChallenge",
      values: [
        {
          label: NO_IMPACT_LABEL,
          value: 0,
          description: "Nessuna sfida rilevata",
        },
        {
          label: LOW_IMPACT_LABEL,
          value: 1,
          description: "Tempi pianificati, carico prevedibile",
        },
        {
          label: MEDIUM_IMPACT_LABEL,
          value: 2,
          description: "Scadenze multiple, priorità bilanciate",
        },
        {
          label: HIGH_IMPACT_LABEL,
          value: 3,
          description:
            "Urgenze frequenti, scadenze concorrenti, ripianificazione continua",
        },
      ],
    },
  ];

  const getEvaluationLabel = (key: string, value: number): string => {
    const dimension = evaluationOptions.find((option) => option.key === key);
    if (!dimension) {
      return EMPTY_STRING;
    }
    const dimensionValue = dimension.values.find((v) => v.value === value);
    if (!dimensionValue) {
      return EMPTY_STRING;
    }
    return dimensionValue.label;
  };

  const getImpactValue = (
    action?: number | null,
    contest?: number | null,
    time?: number | null,
  ): number => {
    const result = (action ?? 0) + (contest ?? 0) + (time ?? 0);
    return result;
  };

  const showFinalCalibrationError = () => {
    const canViewCalibrationError =
      roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS) ||
      roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET);

    return (
      canViewCalibrationError &&
      data?.finalDimensionPercentage != null &&
      data.finalDimensionPercentage !== data.proposedDimensionPercentage
    );
  };

  return (
    <div className="page-break">
      <Accordion
        label={
          <BodyTyp variant="2" fontWeight={FontWeightEnum.SEMIBOLD}>
            {ASSIGNED_GOAL_LABEL}
          </BodyTyp>
        }
        variant={AccordionVariantEnum.SECONDARY}
        leftComponent={
          <IconManager size={IconSizeEnum.SMALL}>
            <TargetIcon />
          </IconManager>
        }
        onToggle={setIsAccordionOpen}
        isOpen={isAccordionOpen}
      >
        <StyledWorkInformationBody withMarginTop>
          <StyledAssignedGoalTable>
            <colgroup>
              <col style={{ width: "28%" }} />
              <col />
              <col />
              <col />
              <col style={{ width: "80px" }} />
              <col />
            </colgroup>
            <thead>
              <StyledTableRow $isHeading>
                {filteredFields.map((item) => {
                  return (
                    <TableHeader
                      key={item.label}
                      align={item.align}
                      label={item.label}
                    />
                  );
                })}
              </StyledTableRow>
            </thead>
            <tbody>
              {data?.goals.map((item) => {
                return (
                  <StyledTableRow key={"row" + item.id}>
                    <TableCell edit={false}>
                      <BodyTyp
                        variant="1"
                        fontWeight={FontWeightEnum.SEMIBOLD}
                        color={theme.colors.purple}
                      >
                        {item.goal ?? EMPTY_STRING}
                      </BodyTyp>
                    </TableCell>
                    <TableCell edit={false} align={TableCellAlignEnum.CENTER}>
                      <BodyTyp
                        variant="1"
                        fontWeight={FontWeightEnum.SEMIBOLD}
                        color={theme.colors.purple}
                      >
                        {getEvaluationLabel(
                          "actionChallenge",
                          item.actionChallenge,
                        )}
                      </BodyTyp>
                    </TableCell>
                    <TableCell edit={false} align={TableCellAlignEnum.CENTER}>
                      <BodyTyp
                        variant="1"
                        fontWeight={FontWeightEnum.SEMIBOLD}
                        color={theme.colors.purple}
                      >
                        {getEvaluationLabel(
                          "contestChallenge",
                          item.contestChallenge,
                        )}
                      </BodyTyp>
                    </TableCell>
                    <TableCell edit={false} align={TableCellAlignEnum.CENTER}>
                      <BodyTyp
                        variant="1"
                        fontWeight={FontWeightEnum.SEMIBOLD}
                        color={theme.colors.purple}
                      >
                        {getEvaluationLabel(
                          "timeChallenge",
                          item.timeChallenge,
                        )}
                      </BodyTyp>
                    </TableCell>
                    <TableCell edit={false} align={TableCellAlignEnum.CENTER}>
                      <StyledImpactBoxWrapper>
                        <ImpactBox
                          value={getImpactValue(
                            item.actionChallenge,
                            item.contestChallenge,
                            item.timeChallenge,
                          )}
                        />
                      </StyledImpactBoxWrapper>
                    </TableCell>
                    <TableCell edit={false} align={TableCellAlignEnum.CENTER}>
                      <BodyTyp
                        variant="1"
                        fontWeight={FontWeightEnum.SEMIBOLD}
                        color={
                          showFinalCalibrationError()
                            ? theme.colors.red_1
                            : theme.colors.purple
                        }
                      >
                        {item.achievementPercent
                          ? `${item.achievementPercent}%`
                          : EMPTY_STRING}
                      </BodyTyp>
                    </TableCell>
                  </StyledTableRow>
                );
              })}
            </tbody>
          </StyledAssignedGoalTable>
        </StyledWorkInformationBody>
      </Accordion>
    </div>
  );
};
